/* tslint:disable: no-require-imports */
import _merge = require('lodash/merge');
import _extend = require('lodash/extend');
import _assign = require('lodash/assign');
import mapKeys = require('lodash/mapKeys');
import mapValues = require('lodash/mapValues');
import pickBy = require('lodash/pickBy');
/* tslint:enable: no-require-imports */

export function merge<T>(base: T, ...objects: object[]): T {
	return _merge<T>({}, base, ...objects);
}

export function extend<T>(base: T, ...objects: object[]): T {
	return _extend<T>({}, base, ...objects);
}

export function assign<T>(base: T, ...objects: object[]): T {
	return _assign<T>({}, base, ...objects);
}

// reference config must not have specical characters in exposure and peril names
const EXPOSURE_NAME_IN_REFERENCE_PATH_REGEX = /^policy\/exposures\/[\w-]+\//;
const PERIL_NAME_IN_REFERENCE_PATH_REGEX = /^policy\/exposures\/[\w-]+\/perils\/[\w-]+\./;
const NAME_AND_DISPLAY_NAME_IN_JSON_REGEX = /"name\":\s\"[\w-]+\",\n\s\s\"displayName\":\s\".+\",/;

const isExposureSpecificFile = (path: string) => EXPOSURE_NAME_IN_REFERENCE_PATH_REGEX.test(path);
const isPerilSpecificFile = (path: string) => PERIL_NAME_IN_REFERENCE_PATH_REGEX.test(path);

function getReplacedExposureFilePath(exposureName: string, path: string) {
	const replacement: string = `policy/exposures/${exposureName}/`;
	return path.replace(EXPOSURE_NAME_IN_REFERENCE_PATH_REGEX, replacement);
}

function getReplacedPerilFilePath(exposureName: string, perilName: string, path: string) {
	const replacement: string = `policy/exposures/${exposureName}/perils/${perilName}.`;
	return path.replace(PERIL_NAME_IN_REFERENCE_PATH_REGEX, replacement);
}

function getNonExposureRelatedFiles(genericProductAssets: ConfigEditor.AssetFiles): ConfigEditor.AssetFiles {
	return pickBy<ConfigEditor.AssetFiles, ConfigEditor.AssetFiles>
		(genericProductAssets, (contents: string, path: string) => {
			return !isExposureSpecificFile(path);
		});
}

function getNonPerilRelatedExposureFiles(genericProductAssets: ConfigEditor.AssetFiles): ConfigEditor.AssetFiles {
	return pickBy<ConfigEditor.AssetFiles, ConfigEditor.AssetFiles>
		(genericProductAssets, (contents: string, path: string) => {
			return isExposureSpecificFile(path) && !isPerilSpecificFile(path);
		});
}

function getPerilRelatedFiles(genericProductAssets: ConfigEditor.AssetFiles): ConfigEditor.AssetFiles {
	return pickBy<ConfigEditor.AssetFiles, ConfigEditor.AssetFiles>
		(genericProductAssets, (contents: string, path: string) => {
			return isPerilSpecificFile(path);
		});
}

function mapExposureFiles(exposureName: string, files: ConfigEditor.AssetFiles): ConfigEditor.AssetFiles {
	const keyMappedExposureFiles = mapKeys(files, (contents: string, path: string) => {
		return getReplacedExposureFilePath(exposureName, path);
	});
	return mapValues(keyMappedExposureFiles, contents => {
		return modifyNameAndDisplayNameInJson(exposureName, contents);
	});
}

function mapPerilFiles(
	exposureName: string,
	perilName: string,
	files: ConfigEditor.AssetFiles,
): ConfigEditor.AssetFiles {
	const keyMappedPerilFiles = mapKeys(files, (contents: string, path: string) => {
		return getReplacedPerilFilePath(exposureName, perilName, path);
	});
	return mapValues(keyMappedPerilFiles, contents => {
		return modifyNameAndDisplayNameInJson(perilName, contents);
	});
}

function modifyNameAndDisplayNameInJson(name: string, contents: string): string {
	const displayName: string = name.replace(/[\W_]+/g, ' ');
	const replacement: string = `\"name\": \"${name}\",\n  \"displayName\": \"${displayName}\",`;
	return contents.replace(NAME_AND_DISPLAY_NAME_IN_JSON_REGEX, replacement);
}

function addProductPrefix(productName: string, files: ConfigEditor.AssetFiles): ConfigEditor.AssetFiles {
	return mapKeys(files, (contents: string, path: string) => {
		return `products/${productName}/${path}`;
	});
}

export function getAddedAssetFiles(
	{ modifying, modification, genericProductAssets }: ConfigEditor.AddAssetsData,
): ConfigEditor.AssetFiles {
	const { productName, exposureName, perilName } = modification;
	if (modifying === 'PRODUCT') {
		const nonExposureRelatedFiles = getNonExposureRelatedFiles(genericProductAssets);
		const exposureRelatedFiles = getNonPerilRelatedExposureFiles(genericProductAssets);
		const perilRelatedFiles = getPerilRelatedFiles(genericProductAssets);
		return addProductPrefix(productName, {
			...nonExposureRelatedFiles,
			...mapExposureFiles(exposureName, exposureRelatedFiles),
			...mapPerilFiles(exposureName, perilName, perilRelatedFiles),
		});
	} else if (modifying === 'EXPOSURE') {
		const exposureRelatedFiles = getNonPerilRelatedExposureFiles(genericProductAssets);
		const perilRelatedFiles = getPerilRelatedFiles(genericProductAssets);
		return addProductPrefix(productName, {
			...mapExposureFiles(exposureName, exposureRelatedFiles),
			...mapPerilFiles(exposureName, perilName, perilRelatedFiles),
		});
	} else if (modifying === 'PERIL') {
		const perilRelatedFiles = getPerilRelatedFiles(genericProductAssets);
		return addProductPrefix(productName, {
			...mapPerilFiles(exposureName, perilName, perilRelatedFiles),
		});
	} else {
		throw new Error('Invalid configuration being added.');
	}
}

function shouldInclude(
	path: string,
	modifying: ConfigEditor.ModificationEntity,
	modification: ConfigEditor.AssetsModification,
) {
	const { productName, exposureName, perilName } = modification;
	if (modifying === 'PRODUCT') {
		const test: string = `products/${productName}/`;
		return path.indexOf(test) === -1;
	} else if (modifying === 'EXPOSURE') {
		const test: string = `products/${productName}/policy/exposures/${exposureName}/`;
		return path.indexOf(test) === -1;
	} else if (modifying === 'PERIL') {
		const test: string = `products/${productName}/policy/exposures/${exposureName}/perils/${perilName}`;
		return path.indexOf(test) === -1;
	} else {
		throw new Error('Invalid configuration being removed.');
	}
}

export function getAssetFilesAfterRemoval(
	previousFiles: ConfigEditor.AssetFiles,
	modifying: ConfigEditor.ModificationEntity,
	modification: ConfigEditor.AssetsModification,
): ConfigEditor.AssetFiles {
	return pickBy<ConfigEditor.AssetFiles, ConfigEditor.AssetFiles>(previousFiles, (contents: string, path: string) => {
		return shouldInclude(path, modifying, modification);
	});
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./ts/store/reducers/config-editor/util.ts